import { splitKeywords } from '@/entities/campaign';
import type { ChangeEvent, Dispatch, KeyboardEvent, SetStateAction } from 'react';
import { useCallback, useState } from 'react';

type UseCampaignCreatorKeywordsArgs = {
  creatorKeywords: string[];
  setCreatorKeywords: Dispatch<SetStateAction<string[]>>;
};

export function useCampaignCreatorKeywords({
  creatorKeywords,
  setCreatorKeywords,
}: UseCampaignCreatorKeywordsArgs) {
  const [creatorKeywordInput, setCreatorKeywordInput] = useState('');

  const appendCreatorKeywords = useCallback(
    (rawValue: string) => {
      const nextKeywords = splitKeywords(rawValue);
      if (nextKeywords.length === 0) return;

      setCreatorKeywords((currentKeywords) => {
        const seen = new Set(currentKeywords.map((keyword) => keyword.toLowerCase()));
        const appendedKeywords = [...currentKeywords];

        nextKeywords.forEach((keyword) => {
          const normalizedKeyword = keyword.toLowerCase();
          if (seen.has(normalizedKeyword)) return;
          seen.add(normalizedKeyword);
          appendedKeywords.push(keyword);
        });

        return appendedKeywords;
      });
    },
    [setCreatorKeywords],
  );

  const handleCreatorKeywordInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const nextValue = event.target.value;

      if (!/[,\n]/.test(nextValue)) {
        setCreatorKeywordInput(nextValue);
        return;
      }

      const valueParts = nextValue.split(/[,\n]/);
      const trailingValue = /[,\n]\s*$/.test(nextValue) ? '' : (valueParts.pop() ?? '');
      appendCreatorKeywords(valueParts.join(','));
      setCreatorKeywordInput(trailingValue);
    },
    [appendCreatorKeywords],
  );

  const commitCreatorKeywordInput = useCallback(() => {
    if (!creatorKeywordInput.trim()) return;
    appendCreatorKeywords(creatorKeywordInput);
    setCreatorKeywordInput('');
  }, [appendCreatorKeywords, creatorKeywordInput]);

  const handleCreatorKeywordKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitCreatorKeywordInput();
        return;
      }

      if (event.key === 'Backspace' && !creatorKeywordInput && creatorKeywords.length > 0) {
        event.preventDefault();
        setCreatorKeywords((currentKeywords) => currentKeywords.slice(0, -1));
      }
    },
    [commitCreatorKeywordInput, creatorKeywordInput, creatorKeywords.length, setCreatorKeywords],
  );

  const handleRemoveCreatorKeyword = useCallback(
    (keywordToRemove: string) => {
      setCreatorKeywords((currentKeywords) =>
        currentKeywords.filter((keyword) => keyword !== keywordToRemove),
      );
    },
    [setCreatorKeywords],
  );

  return {
    commitCreatorKeywordInput,
    creatorKeywordInput,
    creatorKeywords,
    handleCreatorKeywordInputChange,
    handleCreatorKeywordKeyDown,
    handleRemoveCreatorKeyword,
  };
}
